import {WalletPlatform} from '@/enums/WalletPlatform'
import {N3WalletConnectConfig} from '@/config/N3WalletConnectConfig'
import {XWalletConnectConfig} from '@/config/XWalletConnectConfig'
import {WalletAdapter} from './WalletAdapter'
import {
  NeoLineConnection,
  isNeoLineInAppBrowser,
} from '../connection/NeoLineConnection'
import {WalletConnectConnection} from '../connection/WalletConnectConnection'
import {OneGateConnection} from '../connection/OneGateConnection'
import {MetaMaskConnection} from '../connection/MetaMaskConnection'
import {NeoLineProvider} from '../providers/NeoLineProvider'
import {NeonProvider} from '../providers/NeonProvider'
import {OneGateProvider} from '../providers/OneGateProvider'
import {MetaMaskProvider} from '../providers/MetaMaskProvider'
import {NeoN3Network} from '../networks/NeoN3Network'
import {NeoXNetwork} from '../networks/NeoXNetwork'
import {ConnectionStrategy} from '../connection/ConnectionStrategy'
import {isMobileBrowser} from '@/helpers/MobileBrowserHelper'
import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'

const usesWalletConnect = (
  walletPlatform: WalletPlatform,
  implementation: 'n3' | 'neoX'
) =>
  walletPlatform === WalletPlatform.NEON ||
  (walletPlatform === WalletPlatform.ONEGATE && implementation === 'neoX') ||
  (walletPlatform === WalletPlatform.NEOLINE &&
    isMobileBrowser() &&
    !isNeoLineInAppBrowser())

const createConnection = (
  walletPlatform: WalletPlatform,
  implementation: 'n3' | 'neoX',
  network: TWalletNetwork
): ConnectionStrategy => {
  if (usesWalletConnect(walletPlatform, implementation)) {
    const config =
      implementation === 'n3' ? N3WalletConnectConfig : XWalletConnectConfig

    return new WalletConnectConnection(
      () => config.networkFor(network),
      config.methods,
      implementation,
      walletPlatform
    )
  }

  if (walletPlatform === WalletPlatform.ONEGATE) {
    return new OneGateConnection()
  }

  if (walletPlatform === WalletPlatform.METAMASK) {
    return new MetaMaskConnection(network)
  }

  return new NeoLineConnection(implementation, network)
}

export function createWalletAdapter(
  walletPlatform: WalletPlatform,
  implementation: 'n3' | 'neoX',
  network: TWalletNetwork
) {
  const connection = createConnection(walletPlatform, implementation, network)

  const wallet = (() => {
    switch (walletPlatform) {
      case WalletPlatform.NEON:
        return new NeonProvider()

      case WalletPlatform.NEOLINE:
        return new NeoLineProvider()

      case WalletPlatform.ONEGATE:
        return new OneGateProvider()

      case WalletPlatform.METAMASK:
        return new MetaMaskProvider()
    }
  })()

  const networkStrategy =
    implementation === 'n3' ? new NeoN3Network() : new NeoXNetwork()

  return new WalletAdapter(connection, wallet!, networkStrategy)
}
